import { api } from "../base";
import type { IMarkupTier } from "@pulse-ui/codegen/generated_ts/markup_tier";
import type { IClstAccountTier } from "@pulse-ui/codegen/generated_ts/clst_account_tier";

// ── Types ───────────────────────────────────────────────────────────────────

export type MarkupTier = IMarkupTier;
export type AccountTier = IClstAccountTier;

export type TradeSide = "Buy" | "Sell";

export interface Trade {
  TradeID: string;
  OrderID?: string | null;
  Symbol: string;
  Side: TradeSide;
  Price: number | string;
  Quantity: number | string;
  Timestamp: string;
  TransactTime?: string | null;
  Market: string;
  MarketAccount?: string | null;
  SubAccount?: string | null;
  TradeStatus?: string | null;
  TradeSource?: string | null;
  Counterparty?: string | null;
  Group?: string | null;
  MarketTradeID?: string | null;
  User?: string | null;
  Comments?: string | null;
}

export interface GetTradesParams {
  startDate?: string;
  endDate?: string;
  symbol?: string;
  markets?: string[];
  subAccounts?: string[];
  limit?: number;
}

export interface BookOptionsTradeRequest {
  base_asset: string;
  quote_asset: string;
  delivery_asset: string;
  unit_notional_amount: string;
  payoff_type: string;
  strike_price: string;
  payment_ccy: string;
  option_type: string;
  expiry_date: string;
  tick_size: string;
  side: string;
  quantity: string;
  notional_amount: string;
  notional_asset: string;
  premium: string;
  market_account: string;
  counterparty: string;
  sub_account: string;
  initial_margin_pct: string;
  initial_margin_amount: string;
  maintenance_margin_pct: string;
  liquidation_margin_pct: string;
  margin_release_pct: string;
  margin_call_pct: string;
  commission_bps: string;
  commission_currency: string;
  comments: string;
}

export interface FixExecutionReport {
  Symbol: string;
  OrderID: string;
  ClOrdID: string;
  ExecID: string;
  Side: string;
  Currency: string;
  Timestamp: string;
  TransactTime: string;
  ExecType: string;
  OrdStatus: string;
  OrderQty: string;
  OrdType: string;
  Price?: string;
  LeavesQty: string;
  CumQty: string;
  GrossTradeAmt?: string;
  CumAmt: string;
  AvgPx: string;
  AvgPxAllIn?: string;
  TimeInForce: string;
  FeeCurrency?: string;
  CumFee?: string;
  LastPx?: string;
  LastQty?: string;
  LastAmt?: string;
  LastFee?: string;
  LastFeeCurrency?: string;
  LastMarket?: string;
  LastMarketAccount?: string;
  AggressorSide?: string;
  OrdRejReason?: string;
  Text?: string;
  SubAccount?: string;
  Comments?: string;
  Revision: number;
  RequestUser?: string;
}

export interface DealRecord {
  Timestamp: string;
  User: string;
  DealID: string;
  Status: string;
  DisplaySymbol: string;
  Text: string;
}

export interface BookOptionsTradeResponse {
  type: string;
  ts: string;
  data: DealRecord[];
}

export interface BookSpotTradeRequest {
  customer: string;
  customer_account: string;
  symbol: string;
  side: string;
  quantity: number;
  price: number;
  amount: number;
  commission: number;
  fee: number;
  fee_bps: number;
  external_id: string;
  trade_time: string;
  internal_comments: string;
  risk_sub_account: string;
  market: string;
  fee_currency?: string;
}

// The bulk-book types below mirror venues-talos-ext/schemas/talos.yml; the UI
// codegen only reads messages-public, so keep them in sync by hand (tech debt).
/** Per-row outcome. `unknown` means Talos may or may not have booked. */
export type BulkBookRowStatus = "booked" | "rejected" | "unknown" | "not_attempted";

export interface BulkBookSpotTradeResultItem {
  index: number;
  ok: boolean;
  error?: string;
  fix_report?: FixExecutionReport;
  status?: BulkBookRowStatus;
}

export interface BulkBookSpotTradeRequest {
  trades: BookSpotTradeRequest[];
}

/** Terminal/in-progress status of an async bulk-book job. */
export type BulkBookJobStatus =
  | "running"
  | "all_booked"
  | "paused_after_reject"
  | "failed";

/** HTTP 202 body: the batch passed validation + pre-flight and is now executing. */
export interface BulkBookJobAccepted {
  job_id: string;
  total_rows: number;
}

/** Status snapshot polled from GET /bulk-jobs/{job_id}. */
export interface BulkBookJobStatusResponse {
  job_id: string;
  status: BulkBookJobStatus;
  total_rows: number;
  created_at: string;
  completed_at?: string | null;
  results: BulkBookSpotTradeResultItem[];
  not_attempted_indices: number[];
  slack_upload_ok?: boolean | null;
  slack_error?: string | null;
  error?: string | null;
}

/** One row that failed pre-booking validation (HTTP 400). */
export interface BulkBookValidationFailure {
  row_index: number;
  failure_kind: string;
  error: string;
}

export interface BulkBookValidationResponse {
  failures: BulkBookValidationFailure[];
}

export interface BulkBookAssetDelta {
  asset: string;
  delta: string;
}

/** Aggregate credit pre-flight breach for one account (HTTP 400). */
export interface BulkBookPreflightFailure {
  clst_account: string;
  breach_row_index: number;
  reason: string;
  row_indices: number[];
  asset_deltas: BulkBookAssetDelta[];
}

export interface BulkBookPreflightResponse {
  failures: BulkBookPreflightFailure[];
}

export interface CancelCustomerTradeResponse {
  action: string;
  uid?: string | null;
  success: boolean;
  error?: string | null;
}

export interface MarkupTierOption {
  code: string;
  display_name: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

const tradeEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Markup Tiers ──────────────────────────────────────────────────

    /** Fetches all markup tiers. */
    getMarkupTiers: build.query<IMarkupTier[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates a markup tier. */
    createMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Updates a markup tier. */
    updateMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Deletes a markup tier by code. */
    deleteMarkupTier: build.mutation<void, string>({
      query: (tierCode) => ({
        url: "/api/trade/v1/markup-tier",
        method: "DELETE",
        params: { tier_code: tierCode },
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    // ── Account Tiers ─────────────────────────────────────────────────

    /** Fetches all account-tier assignments. */
    getAccountTiers: build.query<IClstAccountTier[], void>({
      query: () => ({ url: "/api/trade/v1/clst-account-tier" }),
      providesTags: ["AccountTiers"],
    }),

    /** Fetches markup tier options (code + display_name) for account-tier dropdowns. */
    getMarkupTierOptions: build.query<MarkupTierOption[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      transformResponse: (response: IMarkupTier[]) =>
        (response ?? []).map((t) => ({
          code: t.code,
          display_name: t.display_name,
        })),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates an account-tier assignment. */
    createAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Updates an account-tier assignment. */
    updateAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Deletes an account-tier assignment. */
    deleteAccountTier: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Books a spot trade via trading-ops. */
    bookSpotTrade: build.mutation<FixExecutionReport, BookSpotTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "spot" },
        body,
      }),
    }),

    /** Books an options trade via trading-ops. */
    bookOptionsTrade: build.mutation<BookOptionsTradeResponse, BookOptionsTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "options" },
        body,
      }),
    }),

    /** Cancels a customer trade by trade ID. */
    cancelCustomerTrade: build.mutation<CancelCustomerTradeResponse, string>({
      query: (tradeId) => ({
        url: `/api/trading-ops/v1/customer-trades/${tradeId}`,
        method: "DELETE",
      }),
    }),

    /**
     * Submits a bulk-book batch. Returns 202 + a job handle once the batch
     * passes synchronous validation and aggregate credit pre-flight; poll
     * `getBulkJobStatus` for execution progress. A 400 carries either a
     * `BulkBookValidationResponse` or `BulkBookPreflightResponse` in `error.data`.
     */
    bulkBookTrades: build.mutation<BulkBookJobAccepted, BulkBookSpotTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trades/bulk",
        method: "POST",
        body,
      }),
    }),

    /** Polls the status of an async bulk-book job. */
    getBulkJobStatus: build.query<BulkBookJobStatusResponse, string>({
      query: (jobId) => ({
        url: `/api/trading-ops/v1/bulk-jobs/${jobId}`,
      }),
    }),

    /** Fetches recent trades from trading-ops-server. */
    getTrades: build.query<Trade[], GetTradesParams>({
      query: ({ startDate, endDate, symbol, markets, subAccounts, limit }) => ({
        url: "/api/trading-ops/v1/trades",
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
          ...(symbol && { symbol }),
          ...(markets && markets.length > 0 && { markets: markets.join(",") }),
          ...(subAccounts &&
            subAccounts.length > 0 && { sub_accounts: subAccounts.join(",") }),
          ...(limit !== undefined && { limit }),
        },
      }),
      transformResponse: (response: Trade[] | { data: Trade[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
      providesTags: ["Trades"],
    }),
  }),
});

export const {
  useGetMarkupTiersQuery,
  useCreateMarkupTierMutation,
  useUpdateMarkupTierMutation,
  useDeleteMarkupTierMutation,
  useGetAccountTiersQuery,
  useGetMarkupTierOptionsQuery,
  useCreateAccountTierMutation,
  useUpdateAccountTierMutation,
  useDeleteAccountTierMutation,
  useCancelCustomerTradeMutation,
  useBookSpotTradeMutation,
  useBookOptionsTradeMutation,
  useBulkBookTradesMutation,
  useGetBulkJobStatusQuery,
  useGetTradesQuery,
  useLazyGetTradesQuery,
} = tradeEndpoints;
